import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FiLink, FiX } from "react-icons/fi";

import { Button } from "~/components/Button";
import { Modal } from "~/components/Modal";
import { MaskedInput } from "~/components/TextInput";
import {
	type GenerateMasterLinkDTO,
	generateMasterLinkSchema,
} from "~/modules/sales/schemas";
import type { PaymentPlan } from "~/modules/sales/types";
import { insertToast } from "~/modules/toasts/services";
import { api } from "~/utils/api";

type Props = {
	plan: PaymentPlan;
};

export const MasterLinkModal: React.FC<Props> = ({ plan }) => {
	const [showModal, setShowModal] = useState<boolean>(false);
	const [generatedLink, setGeneratedLink] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		control,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<GenerateMasterLinkDTO>({
		resolver: zodResolver(generateMasterLinkSchema),
	});

	const handleCloseModal = useCallback(() => {
		setShowModal(false);
		setGeneratedLink(null);
		reset();
	}, [reset]);

	const handleOpenModal = useCallback(() => {
		setShowModal(true);
	}, []);

	const onFormSubmit = useCallback(
		async (data: GenerateMasterLinkDTO) => {
			try {
				let expiresAtDate: Date | null = null;
				if (data.expiresAt) {
					const [dd, mm, yyyy] = data.expiresAt.split("/");
					expiresAtDate = new Date(`${yyyy}-${mm}-${dd}T23:59:59Z`);
				}

				const response = await api.post(
					`/admin/sales/plans/${plan.id}/invites`,
					{
						maxUsages: Number(data.maxUsages),
						expiresAt: expiresAtDate ? expiresAtDate.toISOString() : null,
					},
				);

				const inviteUrl = `${window.location.origin}/checkout-cortesia/${response.data.token}`;
				setGeneratedLink(inviteUrl);

				insertToast({
					title: "Link gerado com sucesso!",
					message: "O link foi criado e já pode ser compartilhado.",
					duration: 3,
				});
			} catch (err: any) {
				insertToast({
					title: "Erro ao gerar link",
					message: err.response?.data?.message || err.message,
					type: "error",
					duration: 3,
				});
			}
		},
		[plan.id],
	);

	const copyToClipboard = useCallback(() => {
		if (generatedLink) {
			navigator.clipboard.writeText(generatedLink);
			insertToast({
				title: "Copiado!",
				message: "Link copiado para a área de transferência.",
				duration: 3,
			});
		}
	}, [generatedLink]);

	return (
		<>
			<button
				type="button"
				className="p-1 rounded bg-slate-100 hover:bg-slate-200 text-gpa-blue-500"
				onClick={handleOpenModal}
				title="Gerar Link Master"
			>
				<FiLink size={20} />
			</button>
			<Modal visible={showModal}>
				<div className="flex flex-col">
					<div className="flex items-center justify-between mb-4">
						<h1 className="text-xl">Gerar Link de Cortesia</h1>
						<button
							type="button"
							className="p-2 rounded-full hover:bg-slate-50"
							onClick={handleCloseModal}
						>
							<FiX size={26} />
						</button>
					</div>

					{!generatedLink ? (
						<form
							onSubmit={handleSubmit(onFormSubmit)}
							className="flex flex-col gap-4"
						>
							<p className="text-sm text-slate-500">
								Preencha as configurações abaixo para gerar um link mestre
								(convite) para o plano {plan.name}.
							</p>

							<div className="flex flex-col gap-2">
								<Controller
									control={control}
									name="maxUsages"
									rules={{ required: "Campo obrigatório" }}
									render={({ field }) => (
										<MaskedInput
											mask="integer"
											label="Limite de Usos"
											placeholder="Ex.: 10"
											value={field.value}
											onChange={field.onChange}
											error={errors.maxUsages?.message}
										/>
									)}
								/>
							</div>

							<div className="flex flex-col gap-2">
								<Controller
									control={control}
									name="expiresAt"
									render={({ field }) => (
										<MaskedInput
											mask="date"
											label="Data Limite de Expiração (opcional)"
											placeholder="dd/mm/yyyy"
											value={field.value}
											onChange={field.onChange}
											error={errors.expiresAt?.message}
										/>
									)}
								/>
							</div>

							<Button loading={isSubmitting}>Gerar Link</Button>
						</form>
					) : (
						<div className="flex flex-col gap-4">
							<p className="text-sm text-slate-500">Link gerado com sucesso:</p>
							<div className="p-3 bg-slate-100 rounded break-all font-mono text-sm">
								{generatedLink}
							</div>
							<Button onClick={copyToClipboard}>Copiar Link</Button>
							<Button onClick={handleCloseModal} mode="outlined">
								Fechar
							</Button>
						</div>
					)}
				</div>
			</Modal>
		</>
	);
};
